/**
 * Pending Invitations Page
 * Displays collaboration invitations with accept/decline functionality
 * Card-based layout with invitation details and action buttons
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Avatar } from 'primereact/avatar';
import { Divider } from 'primereact/divider';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Skeleton } from 'primereact/skeleton';
import { Badge } from 'primereact/badge';
import { Chip } from 'primereact/chip';

import {
    collaborationService,
    getRoleConfig,
    getCommissionModelConfig,
    getContributionLevelConfig,
    formatCurrency,
    formatLargeAmount,
    formatTimeAgo,
    getMilestoneStatus,
    COMMISSION_MODELS
} from '../../services/collaborationService';

import { getLoanProductConfig } from '../../services/marketplaceService';

// Decline reason options
const DECLINE_REASONS = [
    { value: 'too_busy', label: 'Currently too busy' },
    { value: 'commission_low', label: 'Commission too low' },
    { value: 'not_my_expertise', label: 'Not in my expertise area' },
    { value: 'already_committed', label: 'Already committed to other leads' },
    { value: 'location_issue', label: 'Location not suitable' },
    { value: 'other', label: 'Other reason' }
];

const PendingInvitations = () => {
    const navigate = useNavigate();
    const toast = useRef(null);

    // State
    const [loading, setLoading] = useState(true);
    const [invitations, setInvitations] = useState([]);
    const [processingId, setProcessingId] = useState(null);

    // Dialog states
    const [showAcceptDialog, setShowAcceptDialog] = useState(false);
    const [showDeclineDialog, setShowDeclineDialog] = useState(false);
    const [selectedInvitation, setSelectedInvitation] = useState(null);

    // Accept dialog state
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [acceptCommitment, setAcceptCommitment] = useState(false);

    // Decline dialog state
    const [declineReason, setDeclineReason] = useState(null);
    const [declineNotes, setDeclineNotes] = useState('');

    // Helper to get lead data
    const getLeadData = (invitation) => {
        return invitation.lead_snapshot || invitation.lead || invitation.collaboration?.lead_snapshot || {};
    };

    // Helper to get borrower name
    const getBorrowerName = (invitation) => {
        const lead = getLeadData(invitation);
        return lead.masked_name || lead.borrower_name || 'Unknown Borrower';
    };

    // Helper to get loan amount
    const getLoanAmount = (invitation) => {
        const lead = getLeadData(invitation);
        return lead.loan_amount_required || lead.loan_amount || lead.amount || 0;
    };

    // Helper to get product type
    const getProductType = (invitation) => {
        const lead = getLeadData(invitation);
        return lead.product_type_detailed || lead.product_type || 'personal_loan';
    };

    // Helper to get quality grade
    const getQualityGrade = (invitation) => {
        const lead = getLeadData(invitation);
        return lead.quality_grade || 'C';
    };

    // Helper to get location
    const getLocation = (invitation) => {
        const lead = getLeadData(invitation);
        const city = lead.city || '';
        const state = lead.state || '';
        return [city, state].filter(Boolean).join(', ') || 'Unknown Location';
    };

    // Fetch invitations
    const fetchInvitations = useCallback(async () => {
        setLoading(true);
        try {
            const response = await collaborationService.getInvitations();
            if (response.success) {
                const data = response.data?.data || response.data || [];
                setInvitations(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Failed to fetch invitations:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load invitations'
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInvitations();
    }, [fetchInvitations]);

    // Handle accept invitation
    const handleAcceptClick = (invitation) => {
        setSelectedInvitation(invitation);
        setAcceptTerms(false);
        setAcceptCommitment(false);
        setShowAcceptDialog(true);
    };

    const confirmAccept = async () => {
        if (!selectedInvitation || !acceptTerms || !acceptCommitment) return;

        setProcessingId(selectedInvitation.id);
        try {
            const response = await collaborationService.acceptInvitation(selectedInvitation.id);
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Accepted!',
                    detail: 'You have joined the lead deal'
                });
                setShowAcceptDialog(false);
                setSelectedInvitation(null);
                fetchInvitations();

                // Navigate to collaboration details
                if (response.data?.collaboration_id) {
                    navigate(`/app/collaborations/${response.data.collaboration_id}`);
                }
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to accept invitation'
            });
        } finally {
            setProcessingId(null);
        }
    };

    // Handle decline invitation
    const handleDeclineClick = (invitation) => {
        setSelectedInvitation(invitation);
        setDeclineReason(null);
        setDeclineNotes('');
        setShowDeclineDialog(true);
    };

    const confirmDecline = async () => {
        if (!selectedInvitation) return;

        setProcessingId(selectedInvitation.id);
        try {
            const response = await collaborationService.declineInvitation(selectedInvitation.id, {
                reason: declineReason,
                notes: declineNotes
            });
            if (response.success) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Declined',
                    detail: 'Invitation has been declined'
                });
                setShowDeclineDialog(false);
                setSelectedInvitation(null);
                fetchInvitations();
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to decline invitation'
            });
        } finally {
            setProcessingId(null);
        }
    };

    // Render commission details based on model
    const renderCommissionDetails = (invitation) => {
        const model = invitation.commission_model || 'percentage_split';
        const config = getCommissionModelConfig(model);

        return (
            <div className="flex flex-column gap-2">
                <div className="flex align-items-center gap-2">
                    <i className={`pi ${config.icon} text-500`} />
                    <span className="text-600 text-sm">{config.label}</span>
                </div>

                {model === 'percentage_split' && (
                    <div className="text-green-600 font-bold text-xl">
                        {invitation.commission_percentage || 0}% of total
                    </div>
                )}

                {model === 'fixed_amount' && (
                    <div className="text-green-600 font-bold text-xl">
                        {formatCurrency(invitation.commission_fixed_amount || 0)} fixed
                    </div>
                )}

                {model === 'milestone_based' && invitation.milestones && (
                    <div className="flex flex-column gap-1">
                        {invitation.milestones.map((milestone, idx) => (
                            <div key={idx} className="flex justify-content-between align-items-center">
                                <span className="text-600 text-sm">{milestone.name}</span>
                                <span className="text-green-600 font-medium">
                                    {formatCurrency(milestone.amount)}
                                </span>
                            </div>
                        ))}
                        <Divider className="my-2" />
                        <div className="flex justify-content-between align-items-center">
                            <span className="font-medium">Total Potential</span>
                            <span className="text-green-600 font-bold">
                                {formatCurrency(invitation.milestones.reduce((sum, m) => sum + (m.amount || 0), 0))}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Render invitation card
    const renderInvitationCard = (invitation) => {
        const roleConfig = getRoleConfig(invitation.role || 'collaborator');
        const productConfig = getLoanProductConfig(getProductType(invitation));
        const contributionConfig = getContributionLevelConfig(invitation.contribution_level);
        const isProcessing = processingId === invitation.id;

        return (
            <Card key={invitation.id} className="mb-4 shadow-2">
                {/* Header: Inviter Info */}
                <div className="flex align-items-center gap-3 mb-4">
                    <Avatar
                        label={invitation.inviter_name?.charAt(0) || 'U'}
                        size="large"
                        shape="circle"
                        className="bg-primary text-white"
                    />
                    <div className="flex-1">
                        <div className="flex align-items-center gap-2 flex-wrap">
                            <span className="font-semibold text-900">{invitation.inviter_name || 'Unknown User'}</span>
                            <span className="text-500">invited you to collaborate</span>
                        </div>
                        <div className="text-400 text-sm mt-1">
                            <i className="pi pi-clock text-xs mr-1" />
                            {formatTimeAgo(invitation.created_at)}
                        </div>
                    </div>
                    <Tag
                        value="Pending"
                        severity="warning"
                        icon="pi pi-envelope"
                        className="text-xs"
                    />
                </div>

                {/* Lead Preview */}
                <div className="surface-100 border-round p-3 mb-4">
                    <div className="flex align-items-start gap-3">
                        <div
                            className="flex align-items-center justify-content-center border-round"
                            style={{
                                width: '48px',
                                height: '48px',
                                backgroundColor: '#eff6ff',
                                flexShrink: 0
                            }}
                        >
                            <i className={`pi ${productConfig.icon} text-primary text-xl`} />
                        </div>
                        <div className="flex-1">
                            <div className="flex align-items-center gap-2 mb-2 flex-wrap">
                                <Tag value={productConfig.label} severity="info" className="text-xs" />
                                <Tag
                                    value={`Grade ${getQualityGrade(invitation)}`}
                                    severity={getQualityGrade(invitation) <= 'B' ? 'success' : 'secondary'}
                                    className="text-xs"
                                />
                            </div>
                            <div className="font-semibold text-900 mb-1">{getBorrowerName(invitation)}</div>
                            <div className="flex align-items-center gap-3 flex-wrap">
                                <span className="text-green-600 font-bold text-lg">
                                    {formatLargeAmount(getLoanAmount(invitation))}
                                </span>
                                <span className="text-500 text-sm">
                                    <i className="pi pi-map-marker text-xs mr-1" />
                                    {getLocation(invitation)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Your Terms Section */}
                <div className="grid mb-4">
                    <div className="col-12 md:col-6 lg:col-3">
                        <div className="text-500 text-sm mb-1">Your Role</div>
                        <Tag
                            value={roleConfig.label}
                            icon={`pi ${roleConfig.icon}`}
                            style={{
                                backgroundColor: roleConfig.bgColor,
                                color: roleConfig.color,
                                border: 'none'
                            }}
                        />
                    </div>
                    <div className="col-12 md:col-6 lg:col-3">
                        <div className="text-500 text-sm mb-1">Contribution Level</div>
                        <Tag
                            value={contributionConfig?.label || 'Medium'}
                            style={{
                                backgroundColor: contributionConfig?.bgColor || '#FEF9C3',
                                color: contributionConfig?.color || '#EAB308',
                                border: 'none'
                            }}
                        />
                    </div>
                    <div className="col-12 md:col-12 lg:col-6">
                        <div className="text-500 text-sm mb-1">Your Commission</div>
                        {renderCommissionDetails(invitation)}
                    </div>
                </div>

                {/* Inviter's Message */}
                {invitation.notes && (
                    <div className="surface-50 border-round p-3 mb-4 border-left-3 border-primary">
                        <div className="flex align-items-start gap-2">
                            <i className="pi pi-comment text-primary mt-1" />
                            <div>
                                <div className="text-500 text-sm mb-1">Message from {invitation.inviter_name}:</div>
                                <div className="text-700 line-height-3 font-italic">"{invitation.notes}"</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex align-items-center justify-content-between flex-wrap gap-2 pt-2 border-top-1 border-200">
                    <Button
                        label="View Lead Details"
                        icon="pi pi-eye"
                        className="p-button-text p-button-sm"
                        onClick={() => {
                            if (invitation.marketplace_lead_id) {
                                navigate(`/app/marketplace/listing/${invitation.marketplace_lead_id}`);
                            }
                        }}
                    />
                    <div className="flex gap-2">
                        <Button
                            label="Decline"
                            icon="pi pi-times"
                            className="p-button-outlined p-button-danger p-button-sm"
                            onClick={() => handleDeclineClick(invitation)}
                            loading={isProcessing}
                            disabled={isProcessing}
                        />
                        <Button
                            label="Accept Invitation"
                            icon="pi pi-check"
                            className="p-button-success p-button-sm"
                            onClick={() => handleAcceptClick(invitation)}
                            loading={isProcessing}
                            disabled={isProcessing}
                        />
                    </div>
                </div>
            </Card>
        );
    };

    // Empty state
    const renderEmptyState = () => (
        <div className="flex flex-column align-items-center justify-content-center py-8">
            <div className="bg-primary-50 border-circle p-5 mb-4">
                <i className="pi pi-inbox text-primary text-5xl" />
            </div>
            <h3 className="text-900 font-semibold mb-2">No Pending Invitations</h3>
            <p className="text-600 text-center mb-4 line-height-3" style={{ maxWidth: '400px' }}>
                You don't have any lead deal invitations at the moment.
                Browse the marketplace to find leads seeking collaborators!
            </p>
            <div className="flex gap-2">
                <Button
                    label="Browse Marketplace"
                    icon="pi pi-shopping-cart"
                    onClick={() => navigate('/app/marketplace')}
                />
                <Button
                    label="My Lead Deals"
                    icon="pi pi-users"
                    className="p-button-outlined"
                    onClick={() => navigate('/app/collaborations')}
                />
            </div>
        </div>
    );

    // Loading skeleton
    const renderSkeleton = () => (
        <div className="flex flex-column gap-4">
            {[1, 2, 3].map(i => (
                <Card key={i} className="shadow-1">
                    <div className="flex align-items-center gap-3 mb-4">
                        <Skeleton shape="circle" size="48px" />
                        <div className="flex-1">
                            <Skeleton width="200px" height="1rem" className="mb-2" />
                            <Skeleton width="100px" height="0.75rem" />
                        </div>
                        <Skeleton width="80px" height="24px" />
                    </div>
                    <Skeleton width="100%" height="120px" className="mb-4" />
                    <div className="grid mb-4">
                        {[1, 2, 3, 4].map(j => (
                            <div key={j} className="col-6 md:col-3">
                                <Skeleton width="60%" height="1rem" className="mb-2" />
                                <Skeleton width="80px" height="24px" />
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-content-end gap-2">
                        <Skeleton width="100px" height="36px" />
                        <Skeleton width="140px" height="36px" />
                    </div>
                </Card>
            ))}
        </div>
    );

    // Accept Dialog Footer
    const acceptDialogFooter = (
        <div className="flex justify-content-end gap-2 pt-2">
            <Button
                label="Cancel"
                icon="pi pi-times"
                className="p-button-text"
                onClick={() => setShowAcceptDialog(false)}
            />
            <Button
                label="Confirm & Accept"
                icon="pi pi-check"
                className="p-button-success"
                onClick={confirmAccept}
                disabled={!acceptTerms || !acceptCommitment}
                loading={processingId === selectedInvitation?.id}
            />
        </div>
    );

    // Decline Dialog Footer
    const declineDialogFooter = (
        <div className="flex justify-content-end gap-2 pt-2">
            <Button
                label="Cancel"
                icon="pi pi-times"
                className="p-button-text"
                onClick={() => setShowDeclineDialog(false)}
            />
            <Button
                label="Decline Invitation"
                icon="pi pi-times"
                className="p-button-danger"
                onClick={confirmDecline}
                loading={processingId === selectedInvitation?.id}
            />
        </div>
    );

    return (
        <div className="pending-invitations">
            <Toast ref={toast} />

            {/* Page Header */}
            <div className="surface-card border-round shadow-1 p-4 mb-4">
                <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3">
                    <div>
                        <div className="flex align-items-center gap-2 mb-2">
                            <Button
                                icon="pi pi-arrow-left"
                                className="p-button-text p-button-rounded p-button-sm"
                                onClick={() => navigate('/app/collaborations')}
                                tooltip="Back to Lead Deals"
                            />
                            <h2 className="text-900 font-bold m-0 text-2xl">Pending Invitations</h2>
                            {invitations.length > 0 && (
                                <Badge value={invitations.length} severity="warning" className="ml-2" />
                            )}
                        </div>
                        <p className="text-600 m-0 ml-5 line-height-3">
                            Review and respond to lead deal invitations
                        </p>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <Button
                            label="My Lead Deals"
                            icon="pi pi-users"
                            className="p-button-outlined"
                            onClick={() => navigate('/app/collaborations')}
                        />
                        <Button
                            label="Browse Marketplace"
                            icon="pi pi-shopping-cart"
                            onClick={() => navigate('/app/marketplace')}
                        />
                    </div>
                </div>
            </div>

            {/* Invitations List */}
            <div className="invitations-list">
                {loading ? (
                    renderSkeleton()
                ) : invitations.length === 0 ? (
                    renderEmptyState()
                ) : (
                    invitations.map(invitation => renderInvitationCard(invitation))
                )}
            </div>

            {/* Accept Confirmation Dialog */}
            <Dialog
                visible={showAcceptDialog}
                onHide={() => setShowAcceptDialog(false)}
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-check-circle text-green-500" />
                        <span>Accept Invitation</span>
                    </div>
                }
                style={{ width: '500px' }}
                modal
                footer={acceptDialogFooter}
            >
                {selectedInvitation && (
                    <div className="flex flex-column gap-4">
                        {/* Summary */}
                        <div className="surface-100 border-round p-3">
                            <div className="text-900 font-semibold mb-2">Lead Deal Terms</div>
                            <div className="grid">
                                <div className="col-6">
                                    <div className="text-500 text-sm">Your Role</div>
                                    <div className="font-medium">
                                        {getRoleConfig(selectedInvitation.role).label}
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="text-500 text-sm">Commission</div>
                                    <div className="font-medium text-green-600">
                                        {selectedInvitation.commission_model === 'percentage_split'
                                            ? `${selectedInvitation.commission_percentage}%`
                                            : formatCurrency(selectedInvitation.commission_fixed_amount || 0)
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Lead Info */}
                        <div className="flex align-items-center gap-3 p-3 border-1 border-200 border-round">
                            <i className={`pi ${getLoanProductConfig(getProductType(selectedInvitation)).icon} text-primary text-xl`} />
                            <div>
                                <div className="font-medium text-900">{getBorrowerName(selectedInvitation)}</div>
                                <div className="text-green-600 font-bold">{formatLargeAmount(getLoanAmount(selectedInvitation))}</div>
                            </div>
                        </div>

                        {/* Confirmation Checkboxes */}
                        <div className="flex flex-column gap-3">
                            <div className="flex align-items-start gap-2">
                                <Checkbox
                                    inputId="acceptTerms"
                                    checked={acceptTerms}
                                    onChange={(e) => setAcceptTerms(e.checked)}
                                />
                                <label htmlFor="acceptTerms" className="text-700 line-height-3 cursor-pointer">
                                    I understand and agree to the commission structure and terms of this lead deal
                                </label>
                            </div>
                            <div className="flex align-items-start gap-2">
                                <Checkbox
                                    inputId="acceptCommitment"
                                    checked={acceptCommitment}
                                    onChange={(e) => setAcceptCommitment(e.checked)}
                                />
                                <label htmlFor="acceptCommitment" className="text-700 line-height-3 cursor-pointer">
                                    I commit to actively contribute to this lead as per my assigned role
                                </label>
                            </div>
                        </div>

                        <div className="bg-green-50 border-round p-3">
                            <div className="flex align-items-center gap-2 text-green-700">
                                <i className="pi pi-info-circle" />
                                <span className="text-sm">
                                    By accepting, you will join the lead deal team and gain access to lead details.
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>

            {/* Decline Dialog */}
            <Dialog
                visible={showDeclineDialog}
                onHide={() => setShowDeclineDialog(false)}
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-times-circle text-red-500" />
                        <span>Decline Invitation</span>
                    </div>
                }
                style={{ width: '450px' }}
                modal
                footer={declineDialogFooter}
            >
                {selectedInvitation && (
                    <div className="flex flex-column gap-4">
                        <div className="surface-100 border-round p-3">
                            <div className="flex align-items-center gap-3">
                                <i className={`pi ${getLoanProductConfig(getProductType(selectedInvitation)).icon} text-primary text-xl`} />
                                <div>
                                    <div className="font-medium text-900">{getBorrowerName(selectedInvitation)}</div>
                                    <div className="text-500 text-sm">From: {selectedInvitation.inviter_name}</div>
                                </div>
                            </div>
                        </div>

                        <div className="field">
                            <label className="block text-900 font-medium mb-2">
                                Reason for declining (optional)
                            </label>
                            <Dropdown
                                value={declineReason}
                                options={DECLINE_REASONS}
                                onChange={(e) => setDeclineReason(e.value)}
                                placeholder="Select a reason"
                                className="w-full"
                            />
                        </div>

                        <div className="field">
                            <label className="block text-900 font-medium mb-2">
                                Additional notes (optional)
                            </label>
                            <InputTextarea
                                value={declineNotes}
                                onChange={(e) => setDeclineNotes(e.target.value)}
                                rows={3}
                                className="w-full"
                                placeholder="Any message for the inviter..."
                            />
                        </div>

                        <div className="bg-orange-50 border-round p-3">
                            <div className="flex align-items-center gap-2 text-orange-700">
                                <i className="pi pi-info-circle" />
                                <span className="text-sm">
                                    Declining this invitation will notify the lead owner. You can still be invited again later.
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default PendingInvitations;
